import { useRef, useState, useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from "react-redux";
import "./r_header.css";
import { useNavigate } from "react-router-dom";
import { logout } from "../../../features/auth/store/authSlice";
import apiClient from "../../../core/config/api";
import { formatName } from "../../../utils/formatName";
import { RootState } from "../../../app/store";

// Sub-users (role_id 6) get their credits from the root account, not their
// own plan - Upgrade Credits doesn't apply to them, so it's disabled here.
const SUB_USER_ROLE_ID = 6;

interface UserData {
    first_name?: string;
    last_name?: string;
    email?: string;
    profile_image_url?: string;
}

const R_Header = () => {
    const [open, setOpen] = useState(false);
    const [userData, setUserData] = useState<UserData | null>(null);
    const popupRef = useRef<HTMLDivElement | null>(null);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const roleId = useSelector((state: RootState) => state.auth.roleId);
    const isSubUser = roleId === SUB_USER_ROLE_ID;

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (
                popupRef.current &&
                !popupRef.current.contains(e.target as Node)
            ) {
                setOpen(false);
            }
        };

        const handleEscKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                setOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("keydown", handleEscKey);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("keydown", handleEscKey);
        };
    }, []);

    // Fetch user data
    const fetchUserData = useCallback(async () => {
        try {
            const response = await apiClient.get("/account/re/", {
                params: { _t: Date.now() },
            });
            const data = response.data;
            if (data) {
                setUserData({
                    first_name: data.first_name,
                    last_name: data.last_name,
                    email: data.email,
                    profile_image_url: data.profile_image_url,
                });
            }
        } catch (error) {
            // silently fail; header can render without user details
        }
    }, []);

    useEffect(() => {
        fetchUserData();
    }, [fetchUserData]);

    useEffect(() => {
        const refresh = () => fetchUserData();
        window.addEventListener("profileImageUpdated", refresh);
        return () => window.removeEventListener("profileImageUpdated", refresh);
    }, [fetchUserData]);

    const handleSignOut = () => {
        dispatch(logout());
        setOpen(false);
        navigate("/login", { replace: true });
    };

    return (
        <>
            <nav className="r-header pt-3 pb-3">
                <div className="d-flex flex-row justify-content-end">
                    <div className="d-flex flex-row mr-3">
                        {!isSubUser && (
                            <div>
                                <button
                                    type="button"
                                    className="upgrade_credits"
                                    onClick={() => {
                                        // Counted server-side for the HubSpot "Clicked Upgrade" events.
                                        apiClient.post("/account/re/upgrade-credits/clicked").catch(() => {});
                                        navigate("/pricing");
                                        setOpen(false);
                                    }}
                                >
                                    Upgrade Credits
                                </button>
                            </div>
                        )}
                        <div>
                            <img
                                src={userData?.profile_image_url || "/images/popup-img.png"}
                                alt="Profile"
                                className="uploade-profile"
                                style={{ borderRadius: "50%", objectFit: "cover", width: "32px", height: "32px" }}
                                onClick={() => {
                                    const nextOpen = !open;
                                    setOpen(nextOpen);
                                    if (nextOpen) fetchUserData();
                                }}
                            />

                            {open && (
                                <div className="profile-popup popup-animate" ref={popupRef}>
                                    <div className="blue-profile">
                                        <div className="d-flex flex-row ">
                                            <div>
                                                <img
                                                    src={userData?.profile_image_url || "/images/popup-img.png"}
                                                    className="popupimg"
                                                    alt=""
                                                    style={{ borderRadius: "50%", objectFit: "cover", width: "48px", height: "48px" }}
                                                />

                                            </div>
                                            <div className="d-flex flex-row">
                                                <div>
                                                    <p className="ken-hogan">
                                                        {userData ? formatName(`${userData.first_name || ''} ${userData.last_name || ''}`) || 'User' : 'Loading...'}
                                                    </p>
                                                    <p className="ken-hoga-2">
                                                        {userData?.email || ''}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="account-hover-item d-flex flex-row align-items-center"
                                        onClick={() => {
                                            navigate("/my-account");
                                            setOpen(false);
                                        }}>
                                        <div className="account-icon">
                                            <img
                                                src="/images/my-account.png"
                                                alt="My Account"
                                                className="icon-default"
                                            />
                                            <img
                                                src="/images/my-account-active.png"
                                                alt="My Account Active"
                                                className="icon-active"
                                            />
                                        </div>

                                        <p className="account-text">My Account</p>
                                    </div>

                                    <div className="account-hover-item d-flex flex-row align-items-center"
                                        onClick={() => {
                                            navigate("/payment-history");
                                            setOpen(false);
                                        }}>
                                        <div className="account-icon">
                                            <i className="bi bi-receipt"></i>
                                        </div>

                                        <p className="account-text">Payment History</p>
                                    </div>

                                    {/* Members draw on their account owner's credits and can't buy any. */}
                                    {!isSubUser && (
                                        <div className="account-hover-item d-flex flex-row align-items-center"
                                            onClick={() => {
                                                navigate("/credit-billing");
                                                setOpen(false);
                                            }}>
                                            <div className="account-icon">
                                                <i className="bi bi-credit-card-2-front"></i>
                                            </div>

                                            <p className="account-text">Credit Billing</p>
                                        </div>
                                    )}

                                    <div className="account-hover-item-1" onClick={handleSignOut}>
                                        <p className="sign-out-page">
                                            Sign Out
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <div className="r_navbar_border"></div>

            </nav>
        </>
    );
}

export default R_Header;